import { useState, useEffect } from 'react';
import { usersAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDateTime, getRoleColor } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Loader2,
  Shield,
  Mail,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full system access' },
  { value: 'physician', label: 'Physician', description: 'Patient care and forms' },
  { value: 'nurse', label: 'Nurse', description: 'Patient updates and submissions' },
  { value: 'clerk', label: 'Clerk', description: 'Patient registration' },
  { value: 'readonly', label: 'Read Only', description: 'View only access' },
];

export default function UsersPage() {
  const { user: currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to fetch users');
    }
    setLoading(false);
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await usersAPI.update(userId, updates);
      toast.success('User updated');
      fetchUsers();
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await usersAPI.delete(userToDelete.id);
      toast.success('User deleted');
      fetchUsers();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fadeIn p-2" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary mb-1">Administration</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">User Management</h1>
          <p className="text-slate-500 mt-1">
            Manage user accounts and permissions
          </p>
        </div>
      </div>

      {/* Role Legend */}
      <div className="premium-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Role Permissions</h2>
            <p className="text-sm text-slate-500">System access levels</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ROLES.map(role => (
            <div key={role.value} className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors">
              <Badge className={cn('mb-2', getRoleColor(role.value))}>
                {role.label}
              </Badge>
              <p className="text-xs text-slate-500">{role.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="premium-card overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">
            {loading ? 'Loading...' : `${users.length} User${users.length !== 1 ? 's' : ''}`}
          </h2>
        </div>
        <div className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <p className="text-slate-500 text-sm">Loading users...</p>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No users found</h3>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <table className="premium-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">User</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Role</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Created</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-5 py-4 w-[80px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/10">
                            <span className="font-semibold text-sm text-primary">
                              {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{user.full_name}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-slate-500">
                          {user.professional_title || '-'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <Badge className={cn(getRoleColor(user.role))}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="h-4 w-4" />
                          {formatDateTime(user.created_at)}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <Badge className={cn(
                          "rounded-lg",
                          user.is_active 
                            ? "bg-emerald-50 text-emerald-600 border border-emerald-200" 
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        )}>
                          <span className={cn(
                            "w-1.5 h-1.5 rounded-full mr-1.5",
                            user.is_active ? "bg-emerald-500" : "bg-slate-500"
                          )} />
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-5 py-4">
                        {currentUser?.role === 'admin' && user.id !== currentUser.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="rounded-xl hover:bg-slate-50" data-testid={`user-actions-${user.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white border-slate-200">
                              <DropdownMenuItem
                                onClick={() => setEditingUser(user)}
                                className="hover:bg-slate-50"
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600 hover:bg-red-500/10"
                                onClick={() => confirmDelete(user)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit User</DialogTitle>
            <DialogDescription>
              Update user role and status
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/10">
                  <span className="font-bold text-primary">
                    {editingUser.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">{editingUser.full_name}</p>
                  <p className="text-sm text-slate-500">{editingUser.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</label>
                <Select
                  value={editingUser.role}
                  onValueChange={(v) => setEditingUser(prev => ({ ...prev, role: v }))}
                >
                  <SelectTrigger className="rounded-xl bg-white border-slate-200" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">Active Status</p>
                  <p className="text-sm text-slate-500">
                    User can access the system
                  </p>
                </div>
                <Switch
                  checked={editingUser.is_active}
                  onCheckedChange={(checked) => setEditingUser(prev => ({ ...prev, is_active: checked }))}
                  data-testid="edit-user-status"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="ghost" onClick={() => setEditingUser(null)} className="rounded-full">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleUpdateUser(editingUser.id, {
                    role: editingUser.role,
                    is_active: editingUser.is_active,
                  })}
                  className="rounded-full btn-primary-glow"
                  data-testid="save-user-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.full_name}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteUser} 
              className="rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20"
              data-testid="confirm-delete-user"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
